import { useEffect, useRef, useState } from 'react';
import { outcomesApi, BAND } from '../lib/outcomesApi';
import type { OutcomesReport, RiskBand, StudentRisk } from '../lib/outcomesApi';
import { ChartFrame, HBarChart, Meter, StatTile, pct } from '../components/charts';

/**
 * STUDENTS AT RISK — "who is scoring less, and how do we improve them."
 *
 * ⚠ THIS SCREEN IS NOT SORTED BY MARKS, AND THAT IS THE ENTIRE POINT.
 *
 * Sorting by percentage puts a child on 44% who was on 35% above a child on 52% who was on 68%.
 * That is exactly backwards: the first is succeeding, the second is the emergency. So the ordering is
 * by BAND — falling, then low, then rising — and "Improving" is a visually distinct, positive state
 * rather than a shade of failure.
 *
 * ⚠ NO RANK IS SHOWN, ANYWHERE, EVER.
 *
 * This is a NEET/JEE institution. Competitive-exam coaching in India has a serious, documented record
 * of student self-harm, and a screen that ranks children is a screen that will eventually be turned
 * around and shown to one. Positions, leaderboards and predicted ranks are deliberately absent. What
 * is here is: where this child stands against their own class average, which direction they are
 * moving, and what to do about it. If a rank is ever added, it goes to educators only — never to a
 * student view, and never as a number a child could read about themselves.
 *
 * ⚠ NO FEE INFORMATION. Teachers can open this page. A teacher who learns that a struggling child's
 * family is also behind on fees starts, quietly, to treat that child differently. The payload has no
 * money field in it at all.
 *
 * ⚠ THE FILTERS ARE SERVER-SIDE, AND THE LIST IS PAGED. 577 children used to come back in one
 * response — a quarter of a megabyte of JSON and 577 expandable cards — on a screen where nobody has
 * ever scrolled past the first thirty. Filtering in the browser would mean shipping all of them
 * again to hide most of them.
 *
 * ⚠ THERE IS NO ALPHABETICAL SORT, AND THAT IS DELIBERATE. Sorting a triage list by name buries the
 * children who need attention among the ninety who do not. The reason anyone asks for it is that
 * they cannot find one particular child — which is what the search box is for.
 */
export function StudentsAtRiskPage() {
  const [data, setData] = useState<OutcomesReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [days, setDays] = useState(90);
  const [band, setBand] = useState<RiskBand | 'all'>('all');
  const [institutionId, setInstitutionId] = useState<string | null>(null);
  const [classLabel, setClassLabel] = useState<string>('');
  const [search, setSearch] = useState('');
  const [applied, setApplied] = useState('');
  const [take, setTake] = useState(50);
  const [open, setOpen] = useState<string | null>(null);

  /* ⚠ Debounced. Every keystroke firing a request over a 26,000-mark analysis is how a search box
     becomes slower than scrolling. 300 ms is below the threshold where typing feels laggy. */
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setApplied(search.trim()), 300);
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [search]);

  /* Any change to the scope goes back to the first page — a "load more" that appends onto a list
     the user has just filtered away is the classic paging bug. */
  useEffect(() => setTake(50), [days, band, institutionId, classLabel, applied]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    outcomesApi
      .atRisk({
        days,
        institutionId,
        band: band === 'all' ? null : band,
        classLabel: classLabel || null,
        search: applied || null,
        take,
      })
      .then((d) => {
        if (!alive) return;
        setData(d);
        setFailed(false);
        /* The server resolves the default institution; adopt it so the chips light up correctly
           and a later request does not silently switch schools. */
        if (!institutionId && d.institutionId) setInstitutionId(d.institutionId);
      })
      .catch(() => alive && setFailed(true))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [days, band, institutionId, classLabel, applied, take]);

  if (loading && !data) return <p style={{ color: 'var(--ink-3)' }}>Reading the mark books…</p>;
  if (failed || !data) {
    return <div className="card" style={{ padding: 20, color: 'var(--ink-3)' }}>Could not load this.</div>;
  }

  const shown = data.students;
  const more = data.matched - shown.length;

  /* ⚠ SORTED BY THE QUANTITY THE CHART ACTUALLY PLOTS, not by the list's order.
     The list is ordered by band then percentage; this chart plots the gap to the class average,
     which is a different measure. Taking the list's first twelve produced bars of 47.7, 53.8, 44.2,
     49.0 … — a bar chart that looks like noise and invites the reader to conclude nothing. It reads
     as a ranking whatever we intend, so it should be ranked on its own axis. */
  const worst = [...shown]
    .filter((s) => (s.weakSubjects[0]?.gapPct ?? 0) > 0)
    .sort((a, b) => (b.weakSubjects[0]?.gapPct ?? 0) - (a.weakSubjects[0]?.gapPct ?? 0))
    .slice(0, 12);

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <div className="eyebrow" style={{ marginBottom: 6 }}>Outcomes</div>
        <h1 className="h-display" style={{ fontSize: 28 }}>Students who need attention</h1>
        <p style={{ color: 'var(--ink-2)', fontSize: 13, marginTop: 4, maxWidth: '70ch' }}>
          Ordered by what to do first, not by who scored lowest — a child climbing out of a hole is
          not the same problem as a child falling into one.
        </p>
      </div>

      {/* ⚠ ONE INSTITUTION AT A TIME. A Class 2 child and a CSE Sem 5 student do not share a
          curriculum, a mark scale or a meaning of "behind"; ranking them against each other made
          the list longer and every comparison on it weaker. */}
      {/* ⚠ Wraps rather than scrolls. On a phone the strip scrolled, and the SELECTED school was the
          one half off the right edge — so the first thing the screen failed to tell you was whose
          children you were looking at. */}
      {data.institutions.length > 1 && (
        <div className="tab-strip" style={{ gap: 8, marginBottom: 10, borderBottom: 'none', flexWrap: 'wrap', overflowX: 'visible' }}>
          {data.institutions.map((i) => (
            <button
              key={i.id}
              type="button"
              className={'chip' + (i.id === data.institutionId ? ' is-on' : '')}
              onClick={() => { setInstitutionId(i.id); setClassLabel(''); }}
            >
              {i.name}
            </button>
          ))}
        </div>
      )}

      <div className="tab-strip" style={{ gap: 8, marginBottom: 12, borderBottom: 'none', alignItems: 'center', flexWrap: 'wrap' }}>
        <select className="input" value={days} onChange={(e) => setDays(Number(e.target.value))} style={{ minWidth: 150 }}>
          <option value={60}>Last 60 days</option>
          <option value={90}>Last 90 days</option>
          <option value={180}>Last 6 months</option>
          <option value={365}>This year</option>
        </select>

        {/* ⚠ A FILTER, NOT COLLAPSIBLE GROUPS. Twenty closed accordions destroy the worst-first
            ordering: you cannot tell which one to open. The count is the point — it says where the
            problem is concentrated before anybody clicks, and it is counted across the whole
            institution so choosing one class does not empty the others. */}
        <select
          className="input"
          value={classLabel}
          onChange={(e) => setClassLabel(e.target.value)}
          style={{ minWidth: 210 }}
        >
          {/* ⚠ The institution-wide total, not `matched` — `matched` already has this filter in it,
              so using it here would make the option read "Every class (33)" once a class is chosen. */}
          <option value="">
            Every class ({data.classes.reduce((a, c) => a + c.total, 0)} on the list)
          </option>
          {data.classes.map((c) => (
            <option key={c.classLabel} value={c.classLabel}>
              {c.classLabel} — {c.total} on the list{c.urgent > 0 ? `, ${c.urgent} urgent` : ''}
            </option>
          ))}
        </select>

        <input
          className="input"
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Find a child by name or admission number"
          aria-label="Find a child by name or admission number"
          style={{ minWidth: 250, flex: '1 1 250px' }}
        />

        {([['all', 'Everyone'], [BAND.Urgent, 'Urgent'], [BAND.Watch, 'Watch'], [BAND.Improving, 'Improving']] as const).map(
          ([v, label]) => (
            <button
              key={String(v)}
              type="button"
              className={'chip' + (band === v ? ' is-on' : '')}
              onClick={() => setBand(v as RiskBand | 'all')}
            >
              {label}
            </button>
          ),
        )}
      </div>

      <div style={{ opacity: loading ? 0.55 : 1, transition: 'opacity .15s' }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(155px, 1fr))',
            gap: 12,
            marginBottom: 16,
          }}
        >
          <div className="card" style={{ padding: 16 }}>
            <StatTile label="Need attention now" value={String(data.urgent)}
              foot={<span style={{ color: 'var(--danger)' }}>failing or falling fast</span>} />
          </div>
          <div className="card" style={{ padding: 16 }}>
            <StatTile label="Worth watching" value={String(data.watch)}
              foot={<span style={{ color: 'var(--warn)' }}>below par or slipping</span>} />
          </div>
          <div className="card" style={{ padding: 16 }}>
            <StatTile label="Behind but climbing" value={String(data.improving)}
              foot={<span style={{ color: 'var(--ok)' }}>leave the support in place</span>} />
          </div>
          <div className="card" style={{ padding: 16 }}>
            <StatTile label="Students analysed" value={String(data.studentsAnalysed)}
              foot={`${data.from} to ${data.to}`} />
          </div>
        </div>

        {/* ⚠ The four tiles above are the WHOLE institution; the list below is whatever is filtered.
            Without this line a teacher who picks one class reads "118 need attention now" over a
            list of 33 and cannot tell which number is about their class. */}
        {(classLabel || applied || band !== 'all') && (
          <p style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: -6, marginBottom: 14 }}>
            Those four totals are for the whole of{' '}
            {data.institutions.find((i) => i.id === data.institutionId)?.name ?? 'this institution'}.
            The list below is filtered — {data.matched} of them.
          </p>
        )}

        {/* ⚠ THE ANALYSIS RAN ON A PARTIAL SET. Loud, because every number above and below it is
            then computed from an arbitrary slice of the marks, and nothing else on the page would
            look any different. */}
        {data.marksTruncated && (
          <div className="card" style={{ padding: 14, marginBottom: 14, borderLeft: '3px solid var(--danger)' }}>
            <strong style={{ color: 'var(--danger)' }}>These figures are incomplete.</strong>{' '}
            <span style={{ fontSize: 13, color: 'var(--ink-2)' }}>
              There are more marks in this window than one analysis can read, so the percentages,
              trends and class averages on this page were computed from part of the data. Narrow the
              date range and they become exact again.
            </span>
          </div>
        )}

        {/* ⚠ The precision limit, stated on screen rather than left to be assumed. */}
        <p style={{ fontSize: 12, color: 'var(--ink-3)', lineHeight: 1.6, marginBottom: 18, maxWidth: '80ch' }}>
          ⚠ {data.caveat}
        </p>

        {data.classPatterns.length > 0 && (
          <div className="card" style={{ padding: 16, marginBottom: 16 }}>
            <ChartFrame
              title="Shared weaknesses — a teaching signal, not a pupil signal"
              subtitle="Where several children in one class are weak in the same subject, the answer is usually to reteach it rather than to support them one by one."
            >
              <HBarChart
                categories={data.classPatterns.map((p) => `${p.classLabel} · ${p.subject}`)}
                series={[{
                  name: 'Students affected',
                  color: 'var(--s2)',
                  values: data.classPatterns.map((p) => p.studentsAffected),
                }]}
                format={(n) => `${Math.round(n)} students`}
                labelWidth={178}
                valueWidth={86}
              />
            </ChartFrame>
          </div>
        )}

        {worst.length > 0 && (
          <div className="card" style={{ padding: 16, marginBottom: 16 }}>
            <ChartFrame
              title="The twelve widest gaps to a child's own class"
              subtitle="Measured against that class's own average in that subject — 48% in a class averaging 45% is not the same problem as 48% in a class averaging 78%. Widest first, which is a different order from the list below."
            >
              <HBarChart
                categories={worst.map((s) => s.fullName)}
                series={[{
                  name: 'Gap to class average',
                  color: 'var(--s8)',
                  values: worst.map((s) => (s.weakSubjects[0]?.gapPct ?? 0)),
                }]}
                format={(n) => (n <= 0 ? 'at or above' : `${n.toFixed(1)} pts below`)}
                labelWidth={130}
                valueWidth={96}
                colorAt={(i) => bandColor(worst[i].band)}
                onSelect={(i) => setOpen((cur) => (cur === worst[i].studentId ? null : worst[i].studentId))}
              />
            </ChartFrame>
          </div>
        )}

        {shown.length === 0 && (
          <div className="card" style={{ padding: 20, color: 'var(--ink-3)' }}>
            {applied
              ? <>Nobody matching “{applied}” is on this list. That is usually good news — a child who
                  is coping does not appear here.</>
              : <>Nobody in this group. Either the marks are healthy, or no assessments have been
                  entered in this window.</>}
          </div>
        )}

        {shown.length > 0 && (
          <p style={{ fontSize: 12, color: 'var(--ink-3)', marginBottom: 10 }}>
            Showing {shown.length} of {data.matched}, worst first.
          </p>
        )}

        <div style={{ display: 'grid', gap: 10 }}>
          {shown.map((s) => (
            <StudentCard
              key={s.studentId}
              s={s}
              open={open === s.studentId}
              onToggle={() => setOpen((cur) => (cur === s.studentId ? null : s.studentId))}
            />
          ))}
        </div>

        {/* ⚠ "Load more", not infinite scroll. This list is ordered by urgency, so reaching the
            bottom is meaningful information — the children below the fold are the ones who matter
            least. A list that silently regrows forever hides that. */}
        {more > 0 && (
          <div style={{ marginTop: 12, textAlign: 'center' }}>
            <button
              type="button"
              className="btn"
              disabled={loading}
              onClick={() => setTake((t) => t + 50)}
            >
              {loading ? 'Loading…' : `Show ${Math.min(50, more)} more of ${more}`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function StudentCard({ s, open, onToggle }: { s: StudentRisk; open: boolean; onToggle: () => void }) {
  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        style={{
          width: '100%', border: 'none', background: 'none', font: 'inherit', cursor: 'pointer',
          textAlign: 'left', padding: '14px 16px', display: 'flex', gap: 12,
          alignItems: 'center', flexWrap: 'wrap',
        }}
      >
        <span style={{ width: 4, alignSelf: 'stretch', borderRadius: 2, background: bandColor(s.band) }} />

        <span style={{ flex: '1 1 200px', minWidth: 0 }}>
          <span style={{ fontWeight: 650, fontSize: 14.5 }}>{s.fullName}</span>
          <span className="mono" style={{ fontSize: 11, color: 'var(--ink-3)', marginLeft: 8 }}>
            {s.admissionNumber}
          </span>
          <span style={{ display: 'block', fontSize: 12, color: 'var(--ink-2)', marginTop: 2 }}>
            {s.classLabel} · {s.headline}
          </span>
        </span>

        <span style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap' }}>
          <Figure label="Now" value={pct(s.latestPct)} />
          <Figure
            label="Change"
            value={s.delta === null ? '—' : `${s.delta > 0 ? '+' : ''}${s.delta.toFixed(1)}`}
            tone={s.delta === null ? undefined : s.delta > 0 ? 'var(--ok)' : s.delta < 0 ? 'var(--danger)' : undefined}
          />
          <Figure label="Attendance" value={pct(s.attendancePct)} />
          <span className="pill" style={{ background: bandBg(s.band), color: bandColor(s.band), fontSize: 10.5 }}>
            {bandLabel(s.band)}
          </span>
        </span>
      </button>

      {open && (
        <div style={{ padding: '0 16px 16px 32px', borderTop: '1px solid var(--line-2)' }}>
          {s.weakSubjects.length > 0 && (
            <div style={{ marginTop: 14 }}>
              <div className="viz-title" style={{ marginBottom: 8 }}>Where the gap is</div>
              {s.weakSubjects.map((w) => (
                <div key={w.subject} style={{ marginBottom: 10, maxWidth: 460 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, marginBottom: 4 }}>
                    <span>{w.subject}</span>
                    <span style={{ color: 'var(--ink-2)', fontVariantNumeric: 'tabular-nums' }}>
                      {w.studentPct.toFixed(1)}% · class {w.classPct.toFixed(1)}%
                    </span>
                  </div>
                  <Meter
                    value={w.studentPct}
                    color={w.studentPct < 40 ? 'var(--danger)' : w.studentPct < 55 ? 'var(--warn)' : 'var(--s1)'}
                  />
                </div>
              ))}
            </div>
          )}

          <div style={{ marginTop: 14 }}>
            <div className="viz-title" style={{ marginBottom: 8 }}>What to do</div>
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {s.actions.map((a, i) => (
                <li key={i} style={{ fontSize: 13.5, lineHeight: 1.6, marginBottom: 4, color: 'var(--ink)' }}>
                  {a}
                </li>
              ))}
            </ul>
          </div>

          {s.assessmentsMissed > 0 && (
            <p style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 12, marginBottom: 0 }}>
              Missed {s.assessmentsMissed} assessment{s.assessmentsMissed === 1 ? '' : 's'} in this window.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function Figure({ label, value, tone }: { label: string; value: string; tone?: string }) {
  return (
    <span style={{ textAlign: 'right' }}>
      <span style={{ display: 'block', fontSize: 10, color: 'var(--ink-3)', letterSpacing: '.04em' }}>
        {label}
      </span>
      <span style={{ fontSize: 14, fontWeight: 650, color: tone ?? 'var(--ink)', fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </span>
    </span>
  );
}

/* ⚠ Status colour AND a text label together, never colour alone — the band is named on every row. */
function bandLabel(b: RiskBand) {
  return b === BAND.Urgent ? 'Urgent' : b === BAND.Watch ? 'Watch' : b === BAND.Improving ? 'Improving' : 'Steady';
}
function bandColor(b: RiskBand) {
  return b === BAND.Urgent ? 'var(--danger)' : b === BAND.Watch ? 'var(--warn)' : b === BAND.Improving ? 'var(--ok)' : 'var(--ink-3)';
}
function bandBg(b: RiskBand) {
  return b === BAND.Urgent ? 'var(--danger-bg)' : b === BAND.Watch ? 'var(--warn-bg)' : b === BAND.Improving ? 'var(--ok-bg)' : 'var(--line-2)';
}
